import React, { useEffect, useState } from 'react';
import { apiClient } from '../../utils/apiClient';
import { agentApi, apiErrorMessage } from '../../services/agentApi';
import { btn, input } from './agentUi';

interface Props {
  onClose: () => void;
  onStarted: (runId: string) => void;
  /** Preselected (e.g. launched from a project folder). */
  customerId?: string | null;
  projectId?: string | null;
  customerName?: string;
  projectName?: string;
}

/**
 * "Run Arie on this folder with these instructions" (AGENT_MAILBOX_PLAN.md
 * §11 suggestion 3) — the same autonomous runner and trace as an email,
 * without the email.
 */
const ManualRunModal: React.FC<Props> = ({ onClose, onStarted, customerId: presetCustomer, projectId: presetProject, customerName, projectName }) => {
  const [customers, setCustomers] = useState<Array<{ id: string; name: string; company?: string | null }>>([]);
  const [projects, setProjects] = useState<Array<{ id: string; title: string }>>([]);
  const [customerId, setCustomerId] = useState(presetCustomer || '');
  const [projectId, setProjectId] = useState(presetProject || '');
  const [instructions, setInstructions] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (presetCustomer) return;
    apiClient.get<any[]>('/api/customers').then((list) => setCustomers(Array.isArray(list) ? list : [])).catch(() => setCustomers([]));
  }, [presetCustomer]);

  useEffect(() => {
    if (!customerId || presetProject) return;
    apiClient.get<any[]>(`/api/projects?customerId=${customerId}`).then((list) => setProjects(Array.isArray(list) ? list : [])).catch(() => setProjects([]));
  }, [customerId, presetProject]);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const { runId } = await agentApi.manualRun({ customerId: customerId || null, projectId: projectId || null, instructions });
      onStarted(runId);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not start the run'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Run Arie</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Arie works through it on its own under your agency's agent policy and records every step. You'll get a summary when it's done.</p>
        <div className="space-y-3 mt-4">
          {presetCustomer ? (
            <p className="text-sm text-gray-700 dark:text-gray-200">On <strong>{customerName || 'this customer'}</strong>{presetProject ? <> › <strong>{projectName || 'this folder'}</strong></> : null}</p>
          ) : (
            <>
              <label className="block text-xs">
                <span className="text-gray-500">Customer (optional)</span>
                <select className={input} value={customerId} onChange={(e) => { setCustomerId(e.target.value); setProjectId(''); }}>
                  <option value="">— Arie decides from the instructions —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}
                </select>
              </label>
              {customerId && (
                <label className="block text-xs">
                  <span className="text-gray-500">Folder (optional)</span>
                  <select className={input} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                    <option value="">— any —</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </label>
              )}
            </>
          )}
          <label className="block text-xs">
            <span className="text-gray-500">What should Arie do?</span>
            <textarea className={input} rows={5} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. Run the year-over-year on the 2025 and 2026 policies and draft a summary email to the insured." />
          </label>
        </div>
        {error && <p className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button className={btn.secondary} onClick={onClose}>Cancel</button>
          <button className={btn.primary} onClick={start} disabled={busy || !instructions.trim()}>{busy ? 'Starting…' : 'Start run'}</button>
        </div>
      </div>
    </div>
  );
};

export default ManualRunModal;
